import logoComarca from '../assets/images/logo/vertical.png';

export const reportColors = ['FF2052', '007FC8', 'EC8400', '00DBB9'];
export const amount = (value: any): number => Number.isFinite(Number(value)) ? Number(value) : 0;
export const money = (value: any): string => 'Bs ' + amount(value).toLocaleString('es-BO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
export const dateTime = (value: any): string => value ? new Date(value).toLocaleString('es-BO', {
    timeZone: 'America/La_Paz', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false
}) : '-';
export const customerName = (item: any): string => {
    const info = item?.cliente_info?.usuario || item?.cliente_info;
    const name = [info?.first_name, info?.last_name].filter(Boolean).join(' ').trim();
    return name || String(item?.cliente_nombre || '').trim();
};
export const customer = (item: any): string => customerName(item) || 'Sin nombre registrado';

export function paymentSale(payment: any, sales: any[] = []): any {
    const id = typeof payment.venta_cafeteria === 'object' ? payment.venta_cafeteria?.id : payment.venta_cafeteria;
    return payment.venta_cafeteria_info || (typeof payment.venta_cafeteria === 'object' ? payment.venta_cafeteria : null)
        || sales.find(s => id != null && String(s.id) === String(id));
}

export function paymentCustomerName(payment: any, bookings: any[] = [], sales: any[] = []): string {
    const reservationId = typeof payment.cita === 'object' ? payment.cita?.id : payment.cita;
    const reservation = bookings.find(c =>
        (reservationId != null && String(c.id) === String(reservationId))
        || (payment.cita_info?.id != null && String(c.id) === String(payment.cita_info.id))
        || (payment.cita_info?.codigo && c.codigo === payment.cita_info.codigo)
    );
    return customerName(payment) || customerName(payment.cita_info) || customerName(reservation) || customerName(paymentSale(payment, sales));
}

export function paymentReference(payment: any, bookings: any[] = [], sales: any[] = []): string {
    const id = typeof payment.cita === 'object' ? payment.cita?.id : payment.cita;
    const booking = bookings.find(c => id != null && String(c.id) === String(id));
    return payment.referencia_orden || paymentSale(payment, sales)?.codigo || payment.cita_info?.codigo || booking?.codigo
        || (payment.venta_cafeteria || payment.cita ? 'Código de orden no disponible'
            : payment.afiliacion ? 'Afiliación #' + payment.afiliacion : payment.sesion ? 'Servicio #' + payment.sesion : '-');
}
const category = (key: string) => ({ efectivo: 'Efectivo', banco: 'Banco / tarjeta', otro: 'Otros' }[key] || key || 'Otros');

export interface ReportSection {
    title: string;
    sheetName?: string;
    headers: string[];
    rows: (string | number)[][];
    moneyColumns?: number[];
}

export function buildSessionReport(data: any) {
    const session = data.sesion || {};
    const payments = data.pagos || [];
    const movements = data.movimientos || [];
    const bookings = data.citas || [];
    const sales = data.ventas_cafeteria || [];
    const paymentsById = new Map<string, any>(payments.map((p: any) => [String(p.id), p]));
    const movementOrigin = (movement: any): string => {
        if (movement.pago == null) return 'Manual';
        const payment = typeof movement.pago === 'object' ? movement.pago : paymentsById.get(String(movement.pago));
        if (payment?.codigo) return String(payment.codigo);
        return 'Pago #' + (typeof movement.pago === 'object' ? movement.pago.id : movement.pago);
    };
    const config = session.pos_config;
    const hasSales = sales.length > 0 || payments.some((p: any) => p.venta_cafeteria != null);
    const showBookings = config ? Boolean(config.habilitar_citas) || bookings.length > 0 : bookings.length > 0 || !hasSales;
    const showSales = Boolean(config?.habilitar_cafeteria) || hasSales;
    const mixedPOS = showBookings && showSales;
    const bookingProducts = bookings.flatMap((booking: any) =>
        (booking.productos_reserva || []).map((product: any) => ({ booking, product }))
    );
    const typeLabel = showBookings && showSales ? 'Reservas y cafetería' : showSales ? 'Cafetería' : 'Reservas';
    const initial = amount(session.saldo_inicial);
    const entries = movements.filter((m: any) => m.tipo === 'entrada').reduce((n: number, m: any) => n + amount(m.monto), 0);
    const exits = movements.filter((m: any) => m.tipo === 'salida').reduce((n: number, m: any) => n + amount(m.monto), 0);
    const registered = session.saldo_actual == null ? initial + entries - exits : amount(session.saldo_actual);
    const closed = session.estado === 'cerrada' || Boolean(session.fecha_cierre);
    const final = closed && session.saldo_final != null ? amount(session.saldo_final) : null;
    const totalPayments = payments.reduce((n: number, p: any) => n + amount(p.monto), 0);
    const paymentEntries = movements.filter((m: any) => m.tipo === 'entrada' && m.pago != null).reduce((n: number, m: any) => n + amount(m.monto), 0);
    const methods = new Map<string, { name: string; category: string; count: number; total: number }>();
    payments.forEach((p: any) => {
        const key = String(p.tipo_pago);
        const item = methods.get(key) || { name: String(p.tipo_pago_nombre || key), category: category(p.tipo_pago_configuracion?.tipo || 'otro'), count: 0, total: 0 };
        item.count += 1; item.total += amount(p.monto); methods.set(key, item);
    });
    const bookingRows = (items: any[]) => items.map(c => [
        c.codigo || String(c.id), customer(c), dateTime(c.fecha_creacion),
        String(c.estado || '-'), amount(c.total), amount(c.monto_pagado), Math.max(0, amount(c.total) - amount(c.monto_pagado))
    ]);
    const referenceDay = data.resumen?.fecha_reporte || new Intl.DateTimeFormat('en-CA', { timeZone: 'America/La_Paz', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
    const todaysBookings = data.citas_hoy || bookings.filter((c: any) => c.fecha_creacion && new Intl.DateTimeFormat('en-CA', { timeZone: 'America/La_Paz', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(c.fecha_creacion)) === referenceDay);
    const todaysSales = data.ventas_cafeteria_hoy || sales.filter((s: any) => s.fecha_venta && new Intl.DateTimeFormat('en-CA', { timeZone: 'America/La_Paz', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(s.fecha_venta)) === referenceDay);
    const saleRows = (items: any[]) => items.map(s => [
        s.codigo || 'Código de orden no disponible', customerName(s) || (s.cliente ? 'Sin nombre registrado' : 'Venta sin cliente asociado'),
        dateTime(s.fecha_venta), ({ pagado: 'Pagado', parcial: 'Pago parcial', pendiente: 'Pendiente', cancelado: 'Cancelado' } as Record<string, string>)[s.estado_pago] || s.estado_pago || '-',
        amount(s.total), amount(s.monto_pagado), s.estado_pago === 'cancelado' ? 0 : (s.saldo_pendiente == null ? Math.max(0, amount(s.total) - amount(s.monto_pagado)) : amount(s.saldo_pendiente))
    ]);
    const sections: ReportSection[] = [
        { title: 'Registro y conciliación de caja', headers: ['Concepto', 'Importe (Bs)'], moneyColumns: [1], rows: [
            ['Saldo inicial de caja', initial],
            ['Entradas por pagos (ya incluidas en movimientos)', paymentEntries],
            ['Otras entradas de caja', entries - paymentEntries],
            ['Total de entradas de caja', entries],
            ['Total de salidas de caja', exits],
            ['Saldo calculado: inicial + entradas - salidas', initial + entries - exits],
            ['Saldo actual registrado', registered],
            ['Saldo final de cierre', final ?? (closed ? 'No registrado' : 'Pendiente: sesión abierta')],
            ['Diferencia de cierre frente al registro', final == null ? 'Pendiente' : final - registered],
        ] },
        { title: 'Actividad de la sesión', headers: ['Concepto', 'Valor'], rows: [
            ...(showBookings ? [['Reservas registradas (incluye canceladas)', bookings.length], ['Reservas generadas hoy en esta sesión (' + referenceDay + ')', todaysBookings.length]] : []),
            ...(showSales && !mixedPOS ? [['Ventas de cafetería (incluye canceladas)', sales.length], ['Ventas de cafetería realizadas hoy en esta sesión (' + referenceDay + ')', todaysSales.length]] : []),
            ...(mixedPOS ? [['Productos de cafetería asociados a reservas (unidades)', bookingProducts.reduce((total: number, item: any) => total + amount(item.product.cantidad), 0)]] : []),
            ['Pagos registrados', payments.length],
            ['Total de pagos, todos los medios', money(totalPayments)],
            ['Movimientos de caja', movements.length],
        ] },
        { title: 'Tipos de pago', headers: ['Medio de pago', 'Categoría', 'Cantidad', 'Importe (Bs)'], moneyColumns: [3],
            rows: Array.from(methods.values()).map(m => [m.name, m.category, m.count, m.total]) },
        { title: 'Historial completo de pagos', headers: ['Código / fecha', 'Referencia', 'Cliente / empresa', 'Medio', 'Importe (Bs)'], moneyColumns: [4],
            rows: payments.map((p: any) => [String(p.codigo || p.id) + '\n' + dateTime(p.fecha_pago),
                paymentReference(p, bookings, sales),
                paymentCustomerName(p, bookings, sales) || p.afiliacion_empresa || (p.venta_cafeteria && paymentSale(p, sales)?.cliente == null ? 'Venta sin cliente asociado' : 'Sin nombre registrado'), String(p.tipo_pago_nombre || p.tipo_pago || '-'), amount(p.monto)]) },
        { title: 'Movimientos de caja', headers: ['Fecha / usuario', 'Tipo', 'Origen', 'Descripción', 'Importe (Bs)'], moneyColumns: [4],
            rows: movements.map((m: any) => [dateTime(m.fecha) + '\n' + String(m.usuario_nombre || m.usuario || '-'), m.tipo === 'entrada' ? 'Entrada' : 'Salida',
                movementOrigin(m), String(m.descripcion || '-'), amount(m.monto)]) },
        ...(showBookings ? [
            ...(!mixedPOS ? [{ title: 'Reservas registradas en la sesión', sheetName: 'Reservas de sesión', headers: ['Código', 'Cliente', 'Creada', 'Estado', 'Total (Bs)', 'Pagado (Bs)', 'Pendiente (Bs)'], moneyColumns: [4, 5, 6], rows: bookingRows(bookings) }] : []),
            { title: 'Reservas generadas hoy (' + referenceDay + ')', sheetName: 'Reservas de hoy', headers: ['Código', 'Cliente', 'Creada', 'Estado', 'Total (Bs)', 'Pagado (Bs)', 'Pendiente (Bs)'], moneyColumns: [4, 5, 6], rows: bookingRows(todaysBookings) },
        ] : []),
        ...(mixedPOS ? [{
            title: 'Cafetería asociada a las reservas de la sesión', sheetName: 'Cafetería de reservas',
            headers: ['Reserva / cliente', 'Producto', 'Cantidad', 'Precio unitario (Bs)', 'Descuento (Bs)', 'Subtotal (Bs)'],
            moneyColumns: [3, 4, 5],
            rows: bookingProducts.map(({ booking, product }: any) => [
                String(booking.codigo || booking.id) + '\n' + customer(booking), String(product.nombre || 'Producto'),
                amount(product.cantidad), amount(product.precio_unitario), amount(product.monto_descuento), amount(product.subtotal),
            ]),
        }] : []),
        ...(showSales && !mixedPOS ? [
            { title: 'Ventas de cafetería en la sesión', sheetName: 'Ventas de cafetería', headers: ['Código de orden', 'Cliente', 'Fecha de venta', 'Estado', 'Total (Bs)', 'Pagado (Bs)', 'Pendiente (Bs)'], moneyColumns: [4, 5, 6], rows: saleRows(sales) },
            { title: 'Ventas de cafetería realizadas hoy (' + referenceDay + ')', sheetName: 'Ventas de hoy', headers: ['Código de orden', 'Cliente', 'Fecha de venta', 'Estado', 'Total (Bs)', 'Pagado (Bs)', 'Pendiente (Bs)'], moneyColumns: [4, 5, 6], rows: saleRows(todaysSales) },
        ] : []),
        { title: 'Observaciones', headers: ['Momento', 'Observación'], rows: [
            ['Apertura', String(session.observaciones_apertura || 'Sin observaciones')],
            ['Cierre', String(session.observaciones_cierre || (closed ? 'Sin observaciones' : 'Sesión abierta'))]
        ] },
    ];
    return { session, initial, registered, closed, final, totalPayments, sections, typeLabel };
}

export async function loadReportLogo(): Promise<string | null> {
    return new Promise(resolve => {
        const img = new Image();
        img.onload = () => {
            try {
                const canvas = document.createElement('canvas');
                canvas.width = img.naturalWidth; canvas.height = img.naturalHeight;
                const ctx = canvas.getContext('2d');
                if (!ctx) { resolve(null); return; }
                ctx.drawImage(img, 0, 0); resolve(canvas.toDataURL('image/png'));
            } catch { resolve(null); }
        };
        img.onerror = () => resolve(null);
        img.src = logoComarca;
    });
}

export function reportFilename(session: any, extension: string) {
    const key = String(session.codigo || session.id || 'sesion').replace(/[^a-zA-Z0-9_-]/g, '_');
    return 'Reporte_POS_' + key + '.' + extension;
}
